/**
 * Transcription Viewer Component
 * Displays transcription content with audio player, master summary and expandable segments
 * Supports clickable timestamps that seek to that point in the audio
 */

import React, { useState, useCallback, useRef } from 'react';
import { Card, Button, Stack, Flex, useTheme, spacing, textStyles, fontSize } from '@fti/design-system';
import type { SessionContent } from '../../hooks/useS3Content';

export interface TranscriptionViewerProps {
  content: SessionContent | null;
  isLoading?: boolean;
  error?: string | null;
}

/**
 * Parse a timestamp string to seconds
 * Supports formats: HH:MM:SS, H:MM:SS, MM:SS, M:SS
 */
function parseTimestamp(timestamp: string): number | null {
  // Remove any brackets or parentheses
  const cleaned = timestamp.replace(/[\[\]\(\)]/g, '').trim();

  const parts = cleaned.split(':').map(p => parseInt(p, 10));

  if (parts.some(isNaN)) return null;

  if (parts.length === 3) {
    // HH:MM:SS
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  } else if (parts.length === 2) {
    // MM:SS
    return parts[0] * 60 + parts[1];
  }

  return null;
}

/**
 * Regex to match timestamps in various formats
 * Matches: [00:15:30], (1:23:45), 00:15:30, etc.
 */
const TIMESTAMP_REGEX = /(\[?\(?\d{1,2}:\d{2}(?::\d{2})?\)?\]?)/g;

interface TimestampTextProps {
  text: string;
  onTimestampClick: (seconds: number) => void;
  primaryColor: string;
}

/**
 * Component that renders text with clickable timestamps
 */
function TimestampText({ text, onTimestampClick, primaryColor }: TimestampTextProps) {
  const parts = text.split(TIMESTAMP_REGEX);

  return (
    <>
      {parts.map((part, index) => {
        const seconds = parseTimestamp(part);
        if (seconds !== null && TIMESTAMP_REGEX.test(part)) {
          // Reset regex lastIndex
          TIMESTAMP_REGEX.lastIndex = 0;
          return (
            <button
              key={index}
              onClick={(e) => {
                e.preventDefault();
                onTimestampClick(seconds);
              }}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                color: primaryColor,
                cursor: 'pointer',
                textDecoration: 'underline',
                font: 'inherit',
              }}
              title={`Jump to ${part}`}
            >
              {part}
            </button>
          );
        }
        return <span key={index}>{part}</span>;
      })}
    </>
  );
}

/**
 * Displays transcription results with audio player, master summary and expandable segments
 */
export function TranscriptionViewer({
  content,
  isLoading = false,
  error = null,
}: TranscriptionViewerProps) {
  const { theme } = useTheme();
  const audioRef = useRef<HTMLAudioElement>(null);
  const [expandedSegments, setExpandedSegments] = useState<Set<string>>(new Set());
  const [segmentView, setSegmentView] = useState<Record<string, 'summary' | 'transcript'>>({});

  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;
  const primaryColor = theme.colors.primary[600];

  const toggleSegment = useCallback((segmentId: string) => {
    setExpandedSegments((prev) => {
      const next = new Set(prev);
      if (next.has(segmentId)) {
        next.delete(segmentId);
      } else {
        next.add(segmentId);
      }
      return next;
    });
  }, []);

  const setSegmentViewMode = useCallback((segmentId: string, mode: 'summary' | 'transcript') => {
    setSegmentView(prev => ({ ...prev, [segmentId]: mode }));
  }, []);

  const expandAll = useCallback(() => {
    if (content) {
      setExpandedSegments(new Set(content.segments.map((s) => s.id)));
    }
  }, [content]);

  const collapseAll = useCallback(() => {
    setExpandedSegments(new Set());
  }, []);

  // Seek audio to specific time in seconds
  const seekToTime = useCallback((seconds: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = seconds;
      audioRef.current.play().catch(() => {
        // Autoplay might be blocked, that's ok
      });
      // Scroll to audio player
      audioRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  // Loading state
  if (isLoading) {
    return (
      <Card variant="outlined">
        <Card.Body>
          <Flex justify="center" align="center" style={{ minHeight: '200px' }}>
            <Stack gap="sm">
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  border: `3px solid ${theme.colors.border.default}`,
                  borderTopColor: theme.colors.primary[500],
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                }}
              />
              <p style={{ color: mutedColor }}>Loading session content...</p>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </Stack>
          </Flex>
        </Card.Body>
      </Card>
    );
  }

  // Error state
  if (error) {
    return (
      <Card variant="outlined">
        <Card.Body>
          <Stack gap="sm">
            <h3 style={{ margin: 0, color: theme.colors.semantic.error.default }}>
              Error Loading Content
            </h3>
            <p style={{ margin: 0, color: mutedColor }}>{error}</p>
          </Stack>
        </Card.Body>
      </Card>
    );
  }

  // No content state
  if (!content) {
    return (
      <Card variant="outlined">
        <Card.Body>
          <Flex justify="center" align="center" style={{ minHeight: '200px' }}>
            <p style={{ color: mutedColor }}>Select a streamer and session to view content</p>
          </Flex>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Stack gap="md">
      {/* Metadata Header */}
      <Card variant="outlined">
        <Card.Body>
          <Flex justify="between" align="center" wrap="wrap" gap="md">
            <div>
              <span style={{ fontWeight: 600, color: textColor, marginRight: spacing.md }}>
                {content.metadata.streamerName}
              </span>
              <span style={{ color: mutedColor, fontSize: fontSize.sm }}>
                {content.metadata.vodDate}
              </span>
            </div>
          </Flex>
        </Card.Body>
      </Card>

      {/* Audio Player */}
      {content.audioUrl && (
        <Card variant="elevated">
          <Card.Body>
            <Stack gap="sm">
              <h3 style={{ margin: 0, color: textColor, fontSize: fontSize.base }}>Audio</h3>
              <audio
                ref={audioRef}
                controls
                src={content.audioUrl}
                style={{
                  width: '100%',
                  borderRadius: '8px',
                }}
              >
                Your browser does not support the audio element.
              </audio>
              {content.audioUrl && (
                <p style={{ margin: 0, fontSize: fontSize.xs, color: mutedColor }}>
                  Tip: Click on timestamps in the text below to jump to that point in the audio
                </p>
              )}
            </Stack>
          </Card.Body>
        </Card>
      )}

      {/* Master Summary */}
      <Card variant="elevated">
        <Card.Body>
          <Stack gap="md">
            <h3 style={{ margin: 0, color: textColor }}>Comprehensive Summary</h3>
            <div
              style={{
                padding: spacing.md,
                background: theme.colors.background.paper,
                borderRadius: '6px',
                whiteSpace: 'pre-wrap',
                fontSize: fontSize.sm,
                lineHeight: 1.6,
                color: textColor,
                maxHeight: '400px',
                overflow: 'auto',
              }}
            >
              {content.masterSummary ? (
                <TimestampText
                  text={content.masterSummary}
                  onTimestampClick={seekToTime}
                  primaryColor={primaryColor}
                />
              ) : (
                'No summary available'
              )}
            </div>
          </Stack>
        </Card.Body>
      </Card>

      {/* Segments */}
      {content.segments.length > 0 && (
        <Card variant="elevated">
          <Card.Body>
            <Stack gap="md">
              <Flex justify="between" align="center">
                <h3 style={{ margin: 0, color: textColor }}>
                  Segments ({content.segments.length})
                </h3>
                <Flex gap="sm">
                  <Button variant="outline" color="primary" size="sm" onClick={expandAll}>
                    Expand All
                  </Button>
                  <Button variant="outline" color="primary" size="sm" onClick={collapseAll}>
                    Collapse All
                  </Button>
                </Flex>
              </Flex>

              <Stack gap="sm">
                {content.segments.map((segment) => {
                  const isExpanded = expandedSegments.has(segment.id);
                  const viewMode = segmentView[segment.id] || 'summary';
                  const displayText = viewMode === 'summary'
                    ? segment.summary || 'No summary available'
                    : segment.transcript || 'No transcript available';

                  return (
                    <div
                      key={segment.id}
                      style={{
                        border: `1px solid ${theme.colors.border.default}`,
                        borderRadius: '6px',
                        overflow: 'hidden',
                      }}
                    >
                      {/* Segment Header */}
                      <button
                        onClick={() => toggleSegment(segment.id)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          width: '100%',
                          padding: spacing.sm,
                          border: 'none',
                          background: theme.colors.background.paper,
                          cursor: 'pointer',
                          textAlign: 'left',
                        }}
                      >
                        <Flex align="center" gap="sm">
                          <span style={{ fontWeight: 500, color: textColor }}>
                            {segment.title}
                          </span>
                          {segment.startTime && (
                            <span
                              style={{
                                fontSize: fontSize.xs,
                                color: primaryColor,
                                cursor: 'pointer',
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                const seconds = parseTimestamp(segment.startTime!);
                                if (seconds !== null) seekToTime(seconds);
                              }}
                            >
                              {segment.startTime}
                              {segment.endTime && ` - ${segment.endTime}`}
                            </span>
                          )}
                        </Flex>
                        <span style={{ color: mutedColor }}>{isExpanded ? '▼' : '▶'}</span>
                      </button>

                      {/* Segment Content */}
                      {isExpanded && (
                        <div style={{ padding: spacing.md }}>
                          <Flex gap="sm" style={{ marginBottom: spacing.sm }}>
                            <Button
                              variant={viewMode === 'summary' ? 'solid' : 'outline'}
                              color="primary"
                              size="sm"
                              onClick={() => setSegmentViewMode(segment.id, 'summary')}
                            >
                              Summary
                            </Button>
                            <Button
                              variant={viewMode === 'transcript' ? 'solid' : 'outline'}
                              color="primary"
                              size="sm"
                              onClick={() => setSegmentViewMode(segment.id, 'transcript')}
                            >
                              Transcript
                            </Button>
                          </Flex>
                          <div
                            style={{
                              marginTop: spacing.md,
                              padding: spacing.sm,
                              background: theme.colors.background.default,
                              borderRadius: '4px',
                              whiteSpace: 'pre-wrap',
                              fontSize: fontSize.sm,
                              lineHeight: 1.5,
                              color: textColor,
                              maxHeight: '300px',
                              overflow: 'auto',
                            }}
                          >
                            <TimestampText
                              text={displayText}
                              onTimestampClick={seekToTime}
                              primaryColor={primaryColor}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </Stack>
            </Stack>
          </Card.Body>
        </Card>
      )}
    </Stack>
  );
}

export default TranscriptionViewer;
